import { FC } from 'react'

import { Container, Header, SpaceBetween } from '@cloudscape-design/components'
import { KeyValuePairs } from '@aws-northstar/ui'

import ReadGenericTags from '../ControlledFormInputs/GenericTagsRead'
import type { Account, StgRequestState } from '../../types/account'

function formatSsoUserNames(raw: unknown): string {
	if (Array.isArray(raw)) {
		return raw
			.filter((e: { userName?: string }) => String(e?.userName ?? '').trim())
			.map((e: { userName: string; displayName?: string }) =>
				e.displayName?.trim() ? `${e.userName} (${e.displayName})` : e.userName
			)
			.join(', ')
	}
	if (typeof raw === 'string' && raw.trim()) return raw
	return '—'
}

function formatTimestamp(iso: string | undefined): string {
	if (!iso?.trim()) return '—'
	return iso.length > 19 ? iso.slice(0, 19).replace('T', ' ') : iso.replace('T', ' ')
}

function perTypeDetailsBlock(
	details: Record<string, unknown>,
	accountType: string
): Record<string, unknown> {
	const nested = details[accountType]
	if (
		nested &&
		typeof nested === 'object' &&
		!Array.isArray(nested) &&
		('distributionList' in nested || 'platformOwner' in nested)
	) {
		return nested as Record<string, unknown>
	}
	return details
}

function pickField(
	block: Record<string, unknown>,
	root: Record<string, unknown>,
	key: string
): string {
	const v = block[key] ?? root[key]
	if (v === undefined || v === null || v === '') return '—'
	return String(v)
}

type Props = {
	account: Account
	stg: StgRequestState
	showTags?: boolean
}

const StgRequestSummaryView: FC<Props> = ({ account, stg, showTags = true }) => {
	const stgDetails = (stg.details ?? {}) as Record<string, unknown>
	const parentDetails = (account.details ?? {}) as Record<string, unknown>
	const accountType = String(account.type ?? '').trim()
	const stgBlock = perTypeDetailsBlock(stgDetails, accountType)
	const parentBlock = perTypeDetailsBlock(parentDetails, accountType)

	const workloadType = String(
		stgDetails.accountWorkloadType ?? parentDetails.accountWorkloadType ?? 'Non-EKS'
	)

	const summaryRows = [
		[
			{ label: 'Requestor', value: stg.requestorID?.trim() || account.requestorID || '—' },
			{ label: 'Submitted at', value: formatTimestamp(stg.submittedAt) },
			{ label: 'Parent account type', value: accountType || '—' },
			{ label: 'Account workload type', value: workloadType }
		],
		[
			{
				label: 'Distribution list',
				value: pickField(stgBlock, parentBlock, 'distributionList')
			},
			{
				label: 'Platform owner',
				value: pickField(stgBlock, parentBlock, 'platformOwner')
			},
			{
				label: 'Budget threshold alert',
				value: pickField(stgBlock, parentBlock, 'monthlyBudgetCap')
			},
			{
				label: 'SSO user names',
				value: formatSsoUserNames(stgBlock.SSOUserNames ?? parentBlock.SSOUserNames)
			}
		]
	]

	return (
		<SpaceBetween direction="vertical" size="l">
			<Container header={<Header variant="h2">Stage request details</Header>}>
				<KeyValuePairs items={summaryRows} />
			</Container>

			{showTags && account.tagsID ? (
				<Container header={<Header variant="h2">Tags</Header>}>
					<ReadGenericTags tagsId={account.tagsID} />
				</Container>
			) : null}
		</SpaceBetween>
	)
}

export default StgRequestSummaryView
